type EventPayload = {
  isModalOpened: boolean;
};

type EventCallback = (payload: EventPayload) => void;

const handlers = new Map<string, Set<EventCallback>>();
const domListeners = new Map<string, (e: Event) => void>();

function getOrCreateDomListener(event: string): (e: Event) => void {
  let listener = domListeners.get(event);
  if (!listener) {
    listener = (e: Event) => {
      const callbacks = handlers.get(event);
      if (callbacks) {
        const { detail = {} } = e as CustomEvent;
        callbacks.forEach((cb) => cb?.(detail));
      }
    };
    domListeners.set(event, listener);
  }
  return listener;
}

const eventBus = {
  on(event: string, callback: EventCallback) {
    if (!handlers.has(event)) {
      handlers.set(event, new Set());
    }

    const callbacks = handlers.get(event);
    if (callbacks) {
      callbacks.add(callback);
    }

    if (handlers.get(event)?.size === 1) {
      const listener = getOrCreateDomListener(event);
      document.addEventListener(event, listener);
    }
  },
  dispatch(event: string, payload: EventPayload) {
    document.dispatchEvent(new CustomEvent(event, { detail: payload }));
  },
  remove(event: string, callback?: EventCallback) {
    const callbacks = handlers.get(event);
    if (!callbacks) return;

    if (callback) {
      callbacks.delete(callback);
    } else {
      callbacks.clear();
    }

    if (callbacks.size === 0) {
      handlers.delete(event);
      const listener = domListeners.get(event);
      if (listener) {
        document.removeEventListener(event, listener);
        domListeners.delete(event);
      }
    }
  },
};

export default eventBus;
